import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, Users, ShoppingCart, Settings, Package, Tag, User, Menu, X, LogOut, ChevronDown, LayoutDashboard, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useWorkspace } from '../contexts/WorkspaceContext';
interface AdminLayoutProps {
  children: React.ReactNode;
}
export function AdminLayout({
  children
}: AdminLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    setWorkspace
  } = useWorkspace();
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) {
        setSwitcherOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSwitchToStudent = () => {
    setWorkspace('student');
    setSwitcherOpen(false);
    navigate('/student/dashboard');
  };
  const navigation = [{
    group: 'MANAGE PROFILE',
    items: [{
      name: 'Manage Profile',
      href: '/profile',
      icon: User
    }]
  }, {
    group: 'COURSE ADMIN',
    items: [{
      name: 'Manage Courses',
      href: '/courses',
      icon: BookOpen
    }, {
      name: 'Course Sections',
      href: '/sections',
      icon: BookOpen
    }, {
      name: 'Export Course',
      href: '/export-course',
      icon: BookOpen
    }, {
      name: 'Export Users',
      href: '/export-users',
      icon: Users
    }, {
      name: 'Manage Questions',
      href: '/questions',
      icon: BookOpen
    }]
  }, {
    group: 'MANAGEMENT',
    items: [{
      name: 'Users',
      href: '/users',
      icon: Users
    }, {
      name: 'Orders',
      href: '/orders',
      icon: ShoppingCart
    }, {
      name: 'Admin Area',
      href: '/admin-area',
      icon: Settings
    }]
  }, {
    group: 'PACKAGES',
    items: [{
      name: 'Manage Packages',
      href: '/packages',
      icon: Package
    }, {
      name: 'Manage Coupons',
      href: '/coupons',
      icon: Tag
    }]
  }];
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-154">
      {/* Top Bar */}
      <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30" data-id="element-155">
        <div className="flex items-center gap-4" data-id="element-156">
          <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 hover:bg-slate-100 rounded-md lg:hidden" data-id="element-157">
            <Menu className="h-6 w-6 text-slate-600" data-id="element-158" />
          </button>
          <h1 className="text-lg font-semibold text-slate-800 hidden sm:block" data-id="element-159">
            Welcome to Administration Area
          </h1>
          {/* Workspace Switcher in top bar */}
          <div ref={switcherRef} className="relative hidden sm:block" data-id="element-160">
            <button onClick={() => setSwitcherOpen(!switcherOpen)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors text-sm" data-id="element-161">
              <span className="inline-block w-2 h-2 rounded-full bg-green-400" data-id="element-162"></span>
              <span className="font-medium text-slate-700" data-id="element-163">Site Admin</span>
              <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${switcherOpen ? 'rotate-180' : ''}`} data-id="element-164" />
            </button>

            {switcherOpen && <div className="absolute top-full left-0 mt-2 w-52 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden z-50" data-id="element-165">
                <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-100" data-id="element-166">
                  Switch Workspace
                </div>
                <button onClick={() => setSwitcherOpen(false)} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-slate-50 transition-colors bg-slate-50/80" data-id="element-167">
                  <div className="w-7 h-7 rounded-md bg-slate-800 flex items-center justify-center flex-shrink-0" data-id="element-168">
                    <ShieldCheck className="h-4 w-4 text-white" data-id="element-169" />
                  </div>
                  <div className="text-left" data-id="element-170">
                    <p className="font-medium text-slate-800" data-id="element-171">Site Admin</p>
                    <p className="text-xs text-slate-500" data-id="element-172">Currently active</p>
                  </div>
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-green-400" data-id="element-173"></span>
                </button>
                <button onClick={handleSwitchToStudent} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-purple-50 transition-colors" data-id="element-174">
                  <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: '#4A2D8F'
              }} data-id="element-175">
                    <LayoutDashboard className="h-4 w-4 text-white" data-id="element-176" />
                  </div>
                  <div className="text-left" data-id="element-177">
                    <p className="font-medium text-slate-800" data-id="element-178">Student Portal</p>
                    <p className="text-xs text-slate-500" data-id="element-179">
                      Student environment
                    </p>
                  </div>
                </button>
              </div>}
          </div>
        </div>

        <div className="flex items-center gap-4" data-id="element-180">
          <span className="text-sm text-slate-600 hidden sm:inline-block" data-id="element-181">
            Logged in as{' '}
            <span className="font-medium text-slate-900" data-id="element-182">Admin User</span>
          </span>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => window.location.href = 'https://www.desertsageseminars.com'} data-id="element-183">
            <LogOut className="h-4 w-4" data-id="element-184" />
            Log-off
          </Button>
        </div>
      </header>

      <div className="flex flex-1 relative" data-id="element-185">
        {/* Sidebar */}
        <aside className={`
            fixed inset-y-0 left-0 z-20 w-64 bg-slate-900 text-slate-300 transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-auto
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `} data-id="element-186">
          <div className="h-full overflow-y-auto p-4" data-id="element-187">
            <div className="flex items-center justify-between mb-4 lg:hidden" data-id="element-188">
              <span className="text-xl font-bold text-white" data-id="element-189">Menu</span>
              <button onClick={() => setIsSidebarOpen(false)} className="p-2 text-slate-400 hover:text-white" data-id="element-190">
                <X className="h-6 w-6" data-id="element-191" />
              </button>
            </div>

            {/* Logo - full width, no switcher */}
            <div className="mb-6 px-2" data-id="element-192">
              <img src="https://cdn.magicpatterns.com/uploads/ghnAoYdYcsrnAteaBPC6VB/DSS-logo-notext.png" alt="Desert Sage Seminars" className="w-full max-w-[180px] h-auto" data-id="element-193" />
            </div>

            <nav className="space-y-8" data-id="element-194">
              {navigation.map(group => <div key={group.group} data-id="element-195">
                  <h3 className="px-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-196">
                    {group.group}
                  </h3>
                  <div className="space-y-1" data-id="element-197">
                    {group.items.map(item => {
                  const isActive = location.pathname === item.href;
                  return <Link key={item.name} to={item.href} className={`
                            group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors
                            ${isActive ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}
                          `} data-id="element-198">
                          <item.icon className={`mr-3 h-5 w-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} data-id="element-199" />
                          {item.name}
                        </Link>;
                })}
                  </div>
                </div>)}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8 w-full" data-id="element-200">
          <div className="max-w-7xl mx-auto" data-id="element-201">{children}</div>
        </main>

        {/* Mobile Overlay */}
        {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-10 lg:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-202" />}
      </div>
    </div>;
}